import type { SolUiContext, UpdatesAskVisibleSnapshot } from "@/modern/components/chatbox/types";

import { getNotificationCategory } from "../constants/config";
import type { MappedNotificationCounts } from "../types";

/** Where Ask was opened from — drives ui_context. */
export type UpdatesAskOrigin =
  | { kind: "summary" }
  | { kind: "notable" }
  | { kind: "routine" }
  | {
      kind: "category";
      category: string;
      label: string;
      caption?: string;
      searchQuery?: string;
    };

export const UPDATES_SCREEN_LABEL = "Updates";

/** Ask replaces the origin surface, so the AI must not describe the user as still being on it. */
function previouslyOn(surface: string): string {
  return `Before opening Ask Swivu, the user was on ${surface}. They have since left that screen and are now in Ask Swivu, so refer to it in the past tense (e.g. "the updates you were viewing") and never say they are currently on it.`;
}

const SUMMARY_PAGE_INTENT =
  `${previouslyOn("the Updates first screen (Notable / Routine / Ask)")} That inbox showed only their unseen updates (scope=mine, is_seen=false). Answer from the summary: Notable vs Routine. Prefer notify counts and inbox tools. Notable means needs attention; Routine comes from counts with notable=false. Failed async jobs are failures.`;

const NOTABLE_PAGE_INTENT =
  `${previouslyOn("the Notable detail surface in Updates")} That list showed only their unseen Notable updates (scope=mine, is_seen=false, notable=true). Prefer Notable items: failures, decisions, and unusual changes. Do not treat Routine categories as the focus unless the user asks to widen scope. Prefer notify inbox tools with those filters.`;

const ROUTINE_PAGE_INTENT =
  `${previouslyOn("the Routine surface in Updates, browsing activity by category")} That inbox showed only their unseen Routine updates (scope=mine, is_seen=false, notable=false). Prefer category counts and explaining Routine categories. Do not focus on Notable unless the user asks to widen scope. Prefer notify inbox tools with notable=false.`;

/** Category keys are API-canonical (no FE↔API remapping). */
export function toSolCategoryKey(category: string): string | null {
  return category || null;
}

function mapCategoryCounts(
  categories: MappedNotificationCounts["categories"],
): UpdatesAskVisibleSnapshot["category_counts"] {
  return categories
    .filter((c) => c.count > 0)
    .map((c) => {
      const category = toSolCategoryKey(c.value);
      if (!category) return null;
      return { category, count: c.count };
    })
    .filter((entry): entry is { category: string; count: number } => entry != null);
}

export interface BuildUpdatesAskUiContextArgs {
  origin: UpdatesAskOrigin;
  /** Notable partition from counts with notable=true. */
  notableTotal: number;
  notableUnread: number;
  /** Routine partition from counts with notable=false. */
  routineTotal: number;
  routineUnread: number;
  routineCategories: MappedNotificationCounts["categories"];
}

/**
 * Build Updates Ask `ui_context` from the surface Ask was opened from.
 */
export function buildUpdatesAskUiContext(
  args: BuildUpdatesAskUiContextArgs,
): SolUiContext {
  const {
    origin,
    notableTotal,
    notableUnread,
    routineTotal,
    routineUnread,
    routineCategories,
  } = args;

  if (origin.kind === "notable") {
    return {
      screen_label: `${UPDATES_SCREEN_LABEL} · Notable`,
      page_intent: NOTABLE_PAGE_INTENT,
      visible_snapshot: {
        total: notableTotal,
        unread: notableUnread,
        notable: notableTotal,
        category_counts: [],
      },
    };
  }

  if (origin.kind === "routine") {
    return {
      screen_label: `${UPDATES_SCREEN_LABEL} · Routine`,
      page_intent: ROUTINE_PAGE_INTENT,
      visible_snapshot: {
        total: routineTotal,
        unread: routineUnread,
        notable: 0,
        category_counts: mapCategoryCounts(routineCategories),
      },
    };
  }

  if (origin.kind === "category") {
    const count =
      routineCategories.find((c) => c.value === origin.category)?.count ?? 0;
    const captionBit = origin.caption
      ? ` Category meaning: ${origin.caption}.`
      : "";
    const searchBit = origin.searchQuery?.trim()
      ? ` A search filter was active: "${origin.searchQuery.trim()}". Prefer matches within this category.`
      : "";
    return {
      screen_label: `${UPDATES_SCREEN_LABEL} · Routine · ${origin.label}`,
      page_intent:
        `${previouslyOn(`a single Routine category in Updates (${origin.label})`)} That list showed only their unseen updates in this category (scope=mine, is_seen=false, notable=false, category=${origin.category}). Prefer this category's records.${captionBit}${searchBit} Use notify inbox tools with those filters. Widen to other categories or Notable only if the user asks.`,
      visible_snapshot: {
        total: count,
        unread: count,
        notable: 0,
        category_counts: [{ category: origin.category, count }],
        ...(origin.searchQuery?.trim()
          ? { search_query: origin.searchQuery.trim() }
          : {}),
        active_category: {
          category: origin.category,
          label: origin.label,
          ...(origin.caption ? { caption: origin.caption } : {}),
        },
      },
    };
  }

  // summary
  return {
    screen_label: UPDATES_SCREEN_LABEL,
    page_intent: SUMMARY_PAGE_INTENT,
    visible_snapshot: {
      total: notableTotal + routineTotal,
      unread: notableUnread + routineUnread,
      notable: notableTotal,
      category_counts: mapCategoryCounts(routineCategories),
    },
  };
}

/** CommandRail caption while Ask is open — origin visible in the UI. */
export function askSurfaceCaption(origin: UpdatesAskOrigin): string {
  if (origin.kind === "notable") {
    return "About Notable · failures and decisions";
  }
  if (origin.kind === "routine") {
    return "About Routine · activity by category";
  }
  if (origin.kind === "category") {
    return `About ${origin.label} · routine in this category`;
  }
  return "About this inbox · Notable and Routine";
}

/** Compact scope chip shown above the first assistant reply. */
export function askScopeLabel(origin: UpdatesAskOrigin): string {
  if (origin.kind === "notable") return "Scope · Notable";
  if (origin.kind === "routine") return "Scope · Routine";
  if (origin.kind === "category") return `Scope · ${origin.label}`;
  return "Scope · Updates";
}

export type AskEmptySuggestion = {
  /** Pill label and the exact message sent on tap. */
  prompt: string;
};

/**
 * Empty Ask suggestion pills for the surface Ask was opened from.
 * `prompt` is both the pill label and the exact message sent on tap.
 */
export function buildAskEmptySuggestions(
  origin: UpdatesAskOrigin,
  args: {
    notableUnread: number;
    routineUnread: number;
    routineCategories: MappedNotificationCounts["categories"];
  },
): AskEmptySuggestion[] {
  const { routineCategories } = args;

  if (origin.kind === "notable") {
    return [
      { prompt: "What changed in Notable?" },
      { prompt: "What failed since I last checked?" },
      { prompt: "Which notables need my attention first?" },
      { prompt: "Any unusual decisions or changes?" },
    ];
  }

  if (origin.kind === "routine") {
    const top = [...routineCategories]
      .filter((c) => c.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 2);

    const prompts = [
      "What's new in Routine?",
      "Summarize my routine updates",
      ...top.map((entry) => {
        const label = getNotificationCategory(entry.value)?.label ?? entry.value;
        return `What's new in ${label}?`;
      }),
      "Which routine categories are busiest?",
    ];
    return prompts.slice(0, 4).map((prompt) => ({ prompt }));
  }

  if (origin.kind === "category") {
    const label = origin.label;
    const search = origin.searchQuery?.trim();
    return [
      { prompt: `What changed in ${label}?` },
      { prompt: `Summarize my ${label} updates` },
      { prompt: `Anything unusual in ${label}?` },
      search
        ? { prompt: `Explain matches for "${search}"` }
        : { prompt: `What should I do next in ${label}?` },
    ];
  }

  return [
    { prompt: "What happened in my Updates?" },
    { prompt: "What failed since I last checked?" },
    { prompt: "Summarize my unread updates" },
    { prompt: "What needs my attention first?" },
  ];
}
